import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { AlertTriangle } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useTenantDetail,
  useRevokeTenantAgreement,
} from "@/hooks/useTenantQueries";
import { TenantEditForm } from "@/components/tenants/TenantEditForm";
import { AgreementCommitmentsCard } from "@/components/common/AgreementCommitmentsCard";

// ============================================================================
// Component
// ============================================================================

export function TenantEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tenantId = id!;

  const {
    data: tenant,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useTenantDetail(tenantId);
  const revokeAgreement = useRevokeTenantAgreement(tenantId);

  const { hasRole } = usePermissions();
  const canRevoke = hasRole("SuperAdmin");

  const handleSuccess = () => {
    // The bare /tenants/:id path does not exist under this router; App.tsx's catch-all would
    // have bounced a successful save back to /admin instead of the tenant it just edited.
    navigate(`/admin/tenants/${tenantId}`);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {isLoading ? (
        <Skeleton className="h-8 w-64" />
      ) : (
        <PageHeader
          title={`Edit ${tenant?.name ?? ""}`}
          subtitle="Update tenant contact information"
          back={{
            // This page is reached from the tenant detail page, so backing out of the edit returns
            // there rather than skipping past the tenant to the list.
            label: "Back to Tenant",
            onClick: () => navigate(`/admin/tenants/${tenantId}`),
          }}
        />
      )}

      {/* Nothing to edit without the tenant — the form used to render a bare "Tenant not found"
          line with no way to ask again, so a token refresh losing a race or a blip on the initial
          load was a dead end. */}
      {!isLoading && !tenant ? (
        <ErrorFallback error={error as Error} onRetry={() => refetch()} />
      ) : (
        <>
          {/* The tenant is in hand; a failed background refetch is reported here instead of
              replacing the form, the same as UserEditPage. */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Could not refresh tenant details</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  The form below may be showing a stale copy of this tenant.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  {isFetching && <Spinner className="mr-2" />}
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Tenant Details</CardTitle>
              <CardDescription>
                Modify the tenant's contact information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TenantEditForm
                tenant={tenant}
                isLoading={isLoading}
                onSuccess={handleSuccess}
              />
            </CardContent>
          </Card>

          {tenant && (
            <AgreementCommitmentsCard
              statuses={tenant.agreements}
              canRevoke={canRevoke}
              description={`Every acceptance of this commitment by tenant "${tenant.name}" and by each of its active members is removed. Each member is asked to accept it again before the console or a new machine can be used.`}
              isPending={revokeAgreement.isPending}
              onRevoke={(type) => revokeAgreement.mutateAsync(type)}
            />
          )}
        </>
      )}
    </div>
  );
}
